import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FaSave } from "react-icons/fa";
import { API_BASE, apiFetch } from "../utils/api";
import { formatPhoneNumberInput } from '../utils/formatters';
import TermsModal from "../Components/Users/TermsModal";
import { Button } from "react-bootstrap";
import axios from 'axios';
import "../Stylesheets/SignUp.css";

const PROFESSION_OPTIONS = [
  "sales",
  "marketing",
  "customer service",
  "financial services",
  "real estate",
  "healthcare",
  "retail",
  "manufacturing",
];

function SignUp() {
  const navigate = useNavigate();
  const key = process.env.REACT_APP_RAPIDAPI_KEY;
  if (!key) console.error("❌ Missing RapidAPI key: REACT_APP_RAPIDAPI_KEY is not defined.");

  // 🔹 Initial Form State
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    cell_phone: "",
    profession: "",
    title: "",
    brokerage: "",
    location: "",
    mls_provider: "",
    member_mls_id: "",
    profile_picture: "",
    facebook: "",
    x: "",
    linkedin: "",
    license_number: "",
    start_date: "",
    password: "",
    confirm_password: "",
    bio: "",
    specializations: [],
    role: "Agent",
    // 🔹 Brokerage Info
    office_id: "",
    office_business_name: "",
    broker: "",
    office_city: "",
    office_state: "",
    office_street_country: "",
    office_street: "",
    office_zip: "",
    broker_email: "",
    phone_number: "",
  });

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(true); // you can toggle this off on selection
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Agent search state
  const [agentResults, setAgentResults] = useState([]);
  const bridgeTokenRaw = process.env.REACT_APP_DATABRIDGE_KEY;
  const bridgeToken = (bridgeTokenRaw || '').trim();
  const isBridgeTokenValid = /^[A-Za-z0-9]+$/.test(bridgeToken) && bridgeToken.length >= 32;
  const isRealEstateProfession = formData.profession === "real estate";
  

  // 🔹 Debug: Log form data changes
  useEffect(() => {
    console.log("Form Data Updated:", formData);
  }, [formData]);

  useEffect(() => {
    if (isRealEstateProfession) return;

    setFormData((prev) => ({
      ...prev,
      brokerage: "",
      broker: "",
      location: "",
      mls_provider: "",
      member_mls_id: "",
      license_number: "",
      start_date: "",
      office_id: "",
      office_business_name: "",
      office_city: "",
      office_state: "",
      office_street_country: "",
      office_street: "",
      office_zip: "",
      broker_email: "",
      phone_number: "",
    }));
    setAgentResults([]);
    setResults([]);
    setQuery("");
    setShow(true);
  }, [isRealEstateProfession]);

  useEffect(() => {
    if (!show) return;

    setResults([]);
    if (!query || query.trim().length < 2) return;

    const t = setTimeout(async () => {
      try {
        const res = await apiFetch("searchBrokerages", {
          queryParams: { q: query.trim() },
        });

        if (!res.success) throw new Error(res.message || "Search failed");

        const normalized = (res.brokerages || []).map((b) => ({
          id: b.id,
          office_id: b.office_id ?? b.officeId,
          office_business_name:
            b.office_business_name ?? b.officeBusinessName ?? "",
          broker:
            b.broker ?? b.designatedRealtor ?? "",
          office_city:
            b.office_city ?? b.officeStreetCity ?? "",
          office_state:
            b.office_state ?? b.officeStreetState ?? "",
          phone_number: b.phone_number ?? b.phoneNumber ?? "",
          broker_email: b.broker_email ?? b.brokerEmail ?? "",
        }));

        setResults(normalized);
      } catch (e) {
        console.error("Brokerage search failed:", e);
      } finally {
      }
    }, 300);

    return () => clearTimeout(t);
  }, [query, show]);

  // 🔹 Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "cell_phone" ? formatPhoneNumberInput(value) : value;

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  // 🔹 Agent Search Handler
  const handleSearch = async () => {
    const agentName = `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim();
    const location = formData.location.trim();
    if (!agentName) {
      setErrors({ form: "First and last name are required for search." });
      return;
    }
    setAgentResults([]);
    setErrors({});

    // Branch by MLS selection
    if ((formData.mls_provider || '').toLowerCase() === 'stellar') {
      if (!isBridgeTokenValid) {
        console.error('Bridge token missing/invalid. Check REACT_APP_DATABRIDGE_KEY.');
        setErrors({ form: 'Bridge token is missing or invalid. Please check configuration and restart the app.' });
        return;
      }
      try {
        const params = new URLSearchParams();
        params.set('access_token', bridgeToken);
        params.set('MemberFullName', agentName);
        // Try to parse "City, ST" if provided to narrow results
        if (location) {
          const [city, state] = location.split(',').map(s => s.trim());
          if (city) params.set('MemberCity', city);
          if (state) params.set('MemberStateOrProvince', state);
        }

        const url = `https://api.bridgedataoutput.com/api/v2/stellar/agents?${params.toString()}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Bridge API error ${res.status}`);
        const json = await res.json();
        console.log('Bridge search response', json);

        const normalized = (json.bundle || []).map((m) => ({
          source: 'bridge',
          id: m.MemberKey || m.MemberKeyNumeric,
          name: m.MemberFullName || agentName,
          city: [m.MemberCity, m.MemberStateOrProvince].filter(Boolean).join(', ') || location,
          raw: m,
        }));
        setAgentResults(normalized);
      } catch (error) {
        console.error('Bridge search error:', error);
        setErrors({ form: 'Error searching Stellar MLS. Please try again.' });
      }
      return;
    }

    // Default: Zillow search via RapidAPI
    try {
      if (!location) {
        setErrors({ form: "Location is required for Zillow search." });
        return;
      }
      const options = {
        method: 'GET',
        url: 'https://zillow-working-api.p.rapidapi.com/agent/search',
        params: {
          location,
          agentName,
          page: '1'
        },
        headers: {
          'x-rapidapi-key': key,
          'x-rapidapi-host': 'zillow-working-api.p.rapidapi.com'
        }
      };
      const response = await axios.request(options);
      const list = Array.isArray(response.data) ? response.data : response.data.agents || [];
      const normalized = list.map(a => ({
        source: 'zillow',
        name: a.name || agentName,
        city: a.city || location,
        username: a.username,
        agent_link: a.agent_link,
        raw: a,
      }));
      setAgentResults(normalized);
    } catch (error) {
      console.error("Error searching agents:", error);
      setErrors({ form: "Error searching agents. Please try again." });
    }
  };

  // 🔹 Select Agent and Fetch Details
  const handleSelectAgent = async (agent) => {
    // Branch on the agent source to map correctly
    if (agent?.source === 'bridge') {
      const m = agent.raw || {};
      const startDateRaw = m.STELLAR_MemberStatusChangeDate || m.ModificationTimestamp || '';
      const startDate = startDateRaw ? new Date(startDateRaw).toISOString().slice(0,10) : '';

      setFormData((prev) => ({
        ...prev,
        // Identity
        first_name: prev.first_name || (m.MemberFullName ? (m.MemberFullName.split(' ')[0] || '') : ''),
        last_name: prev.last_name || (m.MemberFullName ? (m.MemberFullName.split(' ').slice(1).join(' ') || '') : ''),
        title: m.MemberType || m.STELLAR_MatrixUserType || prev.title,
        // Contact
        email: m.MemberEmail || prev.email,
        cell_phone: formatPhoneNumberInput(m.MemberDirectPhone || m.MemberOfficePhone || ''),
        // Brokerage/Office
        office_id: m.OfficeMlsId || m.OfficeKey || prev.office_id,
        office_business_name: m.OfficeName || prev.office_business_name,
        brokerage: m.OfficeName || prev.brokerage,
        office_city: m.MemberCity || prev.office_city,
        office_state: m.MemberStateOrProvince || prev.office_state,
        office_street_country: (m.MemberStateOrProvince ? 'US' : prev.office_street_country) || prev.office_street_country,
        phone_number: m.MemberOfficePhone || prev.phone_number,
        // Profile
        profile_picture: prev.profile_picture,
        // Licensing & dates
        license_number: m.MemberStateLicense || m.MemberNationalAssociationId || prev.license_number,
        member_mls_id: m.MemberMlsId || prev.member_mls_id,
        start_date: startDate || prev.start_date,
        // Socials not provided by Bridge
        facebook: prev.facebook,
        x: prev.x,
        linkedin: prev.linkedin,
        // Derived
        location: `${m.MemberCity || ''}${m.MemberCity || m.MemberStateOrProvince ? ', ' : ''}${m.MemberStateOrProvince || ''}` || prev.location,
      }));
      setAgentResults([]);

      // Fetch brokerage/office details for Stellar member via Bridge Offices API
      try {
        const officeKey = m.OfficeKey;
        if (officeKey && isBridgeTokenValid) {
          const officeUrl = `https://api.bridgedataoutput.com/api/v2/stellar/offices/${officeKey}?access_token=${encodeURIComponent(bridgeToken)}`;
          const resp = await fetch(officeUrl);
          if (resp.ok) {
            const officeJson = await resp.json();
            // Bridge single resource may return { bundle: obj } or { bundle: [obj] } or object
            let o = null;
            if (officeJson && officeJson.bundle) {
              o = Array.isArray(officeJson.bundle) ? officeJson.bundle[0] : officeJson.bundle;
            } else {
              o = officeJson;
            }
            if (o) {
              setFormData((prev) => ({
                ...prev,
                office_business_name: o.OfficeName || prev.office_business_name,
                brokerage: o.OfficeName || prev.brokerage,
                office_id: o.OfficeMlsId || prev.office_id,
                office_city: o.OfficeCity || prev.office_city,
                office_state: o.OfficeStateOrProvince || prev.office_state,
                office_street: o.OfficeAddress1 || prev.office_street,
                office_zip: o.OfficePostalCode || prev.office_zip,
                phone_number: o.OfficePhone || prev.phone_number,
                broker_email: o.OfficeEmail || prev.broker_email,
                office_street_country: (o.OfficeStateOrProvince ? 'US' : prev.office_street_country) || prev.office_street_country,
                location: `${o.OfficeCity || ''}${(o.OfficeCity || o.OfficeStateOrProvince) ? ', ' : ''}${o.OfficeStateOrProvince || ''}` || prev.location,
              }));

              // If the office lists a designated broker MLS ID, fetch broker member details
              if (o.OfficeBrokerMlsId) {
                try {
                  const brokerUrl = `https://api.bridgedataoutput.com/api/v2/stellar/agents?access_token=${encodeURIComponent(bridgeToken)}&MemberMlsId=${encodeURIComponent(o.OfficeBrokerMlsId)}`;
                  const brokerResp = await fetch(brokerUrl);
                  if (brokerResp.ok) {
                    const brokerJson = await brokerResp.json();
                    const brokerBundle = Array.isArray(brokerJson?.bundle)
                      ? brokerJson.bundle
                      : brokerJson?.bundle
                        ? [brokerJson.bundle]
                        : [];
                    const brokerMember = brokerBundle[0];
                    if (brokerMember) {
                      setFormData((prev) => ({
                        ...prev,
                        // Populate the broker (Designated Realtor) field with the broker's full name
                        broker: brokerMember.MemberFullName || prev.broker,
                      }));
                    }
                  } else {
                    console.warn('Bridge Agents API (broker) error', brokerResp.status);
                  }
                } catch (e) {
                  console.warn('Failed to fetch Stellar broker details:', e);
                }
              }
            }
          } else {
            console.warn('Bridge Offices API error', resp.status);
          }
        }
      } catch (e) {
        console.warn('Failed to fetch Stellar office details:', e);
      }
      return;
    }

    // Default: Zillow flow via RapidAPI
    try {
      const options = {
        method: 'GET',
        url: 'https://zillow-working-api.p.rapidapi.com/agent/details',
        params: {
          agent_link: agent.agent_link,
          username: agent.username
        },
        headers: {
          'x-rapidapi-key': key,
          'x-rapidapi-host': 'zillow-working-api.p.rapidapi.com'
        }
      };
      const response = await axios.request(options);
      const details = response.data.agent_details;
      const memberSince = details.professionalInformation?.find(pi => pi.term === "Member since")?.description;
      const startDate = memberSince ? new Date(memberSince).toISOString().split('T')[0] : '';

      setFormData((prev) => ({
        ...prev,
        email: details.email || prev.email,
        cell_phone: formatPhoneNumberInput(details.phoneNumbers?.cell || details.phoneNumbers?.business || ''),
        brokerage: details.businessName || '',
        office_business_name: details.businessName || '',
        broker: details.broker || '',
        office_city: details.businessAddress?.city || '',
        office_state: details.businessAddress?.state || '',
        office_street_country: details.businessAddress?.postalCode ? 'US' : '',
        phone_number: details.phoneNumbers?.brokerage || '',
        profile_picture: details.profilePhotoSrc || '',
        facebook: details.descriptions?.facebookUrl?.[0] || '',
        x: details.descriptions?.twitterUrl?.[0] || details.descriptions?.xUrl?.[0] || '',
        linkedin: details.descriptions?.linkedInUrl?.[0] || '',
        license_number: details.agentLicenses?.[0]?.text || '',
        start_date: startDate,
        bio: details.descriptions?.aboutMe || '',
        specializations: details.descriptions?.specialties || [],
        location: `${details.businessAddress?.city || ''}, ${details.businessAddress?.state || ''}` || prev.location,
      }));
      setAgentResults([]);
    } catch (error) {
      console.error("Error fetching agent details:", error);
      setErrors({ form: "Error fetching agent details. Please try again." });
    }
  };

  // 🔹 Form Validation
  const validateForm = () => {
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "cell_phone",
      "profession",
      "title",
      "password",
      "confirm_password"
    ];

    if (isRealEstateProfession) {
      requiredFields.push("license_number", "start_date");
    }

    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = "This field is required.";
      }
    });

    if (!agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions.";
    }

    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Submit Form
  const submitFormData = async () => {
    // Build brokerage payload for backend upsert
    const brokeragePayload = !isRealEstateProfession
      ? null
      : (formData.office_id || formData.office_business_name)
      ? {
          office_id: formData.office_id || '',
          office_business_name: formData.office_business_name || formData.brokerage || '',
          broker: formData.broker || '',
          office_city: formData.office_city || '',
          office_state: formData.office_state || '',
          office_street: formData.office_street || '',
          office_zip: formData.office_zip || '',
          phone_number: formData.phone_number || '',
          broker_email: formData.broker_email || '',
        }
      : (formData.brokerage || formData.office_business_name || '');

    const formattedData = {
      ...formData,
      profession: isRealEstateProfession ? "RE" : formData.profession,
      brokerage: brokeragePayload,
      member_mls_id: isRealEstateProfession ? (formData.member_mls_id || "") : "",
      start_date: formData.start_date
        ? new Date(formData.start_date).toISOString().split("T")[0]
        : "",
      specializations: formData.specializations,
      license_number: isRealEstateProfession ? formData.license_number : "",
    };
    console.log("Formatted Data:", formattedData);

    try {
      setSubmitting(true);
      const result = await apiFetch("signup", {
        method: "POST",
        body: new URLSearchParams({
          payload: JSON.stringify(formattedData),
        }),
      });

      if (result.success) {
        try {
          submitStripeCheckoutForm({
            email: formData.email,
            userId: result.user?.id,
            publicUserId: result.user?.userId,
          });
          return;
        } catch (stripeError) {
          navigate("/login", {
            state: {
              info: `Account created. ${stripeError.message} Verify your email, then sign in and start billing from Account.`,
            },
          });
          return;
        }
      } else {
        setErrors({ form: result.message });
      }
    } catch (error) {
      console.error("❌ Network Error:", error);
      setErrors({ form: `Network error: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  // 🔹 Handle Form Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (validateForm()) {
      submitFormData();
    }
  };

  const submitStripeCheckoutForm = ({ email, userId, publicUserId }) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${API_BASE}/api/checkout.php`;

    const fields = {
      email: email || "",
      user_id: userId || "",
      public_user_id: publicUserId || "",
      success_path: "/login",
      cancel_path: "/login",
      trial_days: "14",
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div className="signup-wrapper py-3">
      <div className="container">
        <form onSubmit={handleSubmit} className="signup-card card border-0 shadow-sm">
          <div className="card-body p-4">
            <div className="signup-header text-center mb-4">
              <div className="signup-header__nav">
                <Link to="/" className="signup-header__home-link">
                  Back to home
                </Link>
              </div>
              <h2 className="mb-2">Create Your Account</h2>
              <p className="text-muted mb-0">
                Complete the form to set up your low-cost SIGNL CRM account and connect your own AI agents or bots when you are ready.
              </p>
            </div>

            {/* Contact Details */}
            <section className="signup-section mb-3">
              <div className="signup-section__header">
                <h5 className="mb-1">Contact Information</h5>
                <p className="text-muted mb-0">How clients and teammates can reach you.</p>
              </div>
              <div className="row g-1 mt-1">
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="first_name" label="First Name" value={formData.first_name} onChange={handleInputChange} errors={errors} />
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="last_name" label="Last Name" value={formData.last_name} onChange={handleInputChange} errors={errors} />
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="email" label="Email" type="email" value={formData.email} onChange={handleInputChange} errors={errors} />
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="cell_phone" label="Mobile Phone" value={formData.cell_phone} onChange={handleInputChange} errors={errors} />
                <div className="col-12 col-md-6 col-xl-4">
                  <label className="form-label">Profession</label>
                  <select
                    className={`form-select form-select-sm ${errors?.profession ? "is-invalid" : ""}`}
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Profession</option>
                    {PROFESSION_OPTIONS.map((professionOption) => (
                      <option key={professionOption} value={professionOption}>
                        {professionOption}
                      </option>
                    ))}
                  </select>
                  {errors?.profession && <div className="invalid-feedback">{errors.profession}</div>}
                </div>
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="title" label="Professional Title" value={formData.title} onChange={handleInputChange} errors={errors} />
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="facebook" label="Facebook URL" value={formData.facebook} onChange={handleInputChange} errors={errors} />
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="x" label="X (Twitter) URL" value={formData.x} onChange={handleInputChange} errors={errors} />
                <FormInput colClass="col-12 col-md-6 col-xl-4" name="linkedin" label="LinkedIn URL" value={formData.linkedin} onChange={handleInputChange} errors={errors} />
              </div>
            </section>

            {isRealEstateProfession && (
              <section className="signup-section mb-3">
                <div className="signup-section__header">
                  <h5 className="mb-1">Agent Lookup</h5>
                  <p className="text-muted mb-0">Search existing MLS records to fast-fill your profile (optional).</p>
                </div>
                <div className="row g-1 mt-1">
                  <FormInput colClass="col-12 col-md-6 col-xl-4" name="location" label="Location (City, ST)" value={formData.location} onChange={handleInputChange} errors={errors} />
                  <div className="col-12 col-md-6 col-xl-4">
                    <label className="form-label">MLS Provider</label>
                    <select
                      className="form-select form-select-sm"
                      name="mls_provider"
                      value={formData.mls_provider || ""}
                      onChange={handleInputChange}
                    >
                      <option value="">Select MLS</option>
                      <option value="Not Listed">Not Listed</option>
                      <option value="Stellar">Stellar</option>
                    </select>
                  </div>
                </div>

                {formData?.mls_provider === "Stellar" && (
                  <div className="d-flex justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleSearch}
                    >
                      Search Agent
                    </button>
                  </div>
                )}

                {agentResults.length > 0 && (
                  <div className="signup-results card mt-3">
                    <div className="card-body p-0">
                      <ul className="list-group list-group-flush">
                        {agentResults.map((agent, index) => (
                          <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{agent.name || `${formData.first_name} ${formData.last_name}`}</strong>
                              <div className="text-muted small">{agent.city || formData.location}</div>
                            </div>
                            <Button variant="primary" size="sm" onClick={() => handleSelectAgent(agent)}>
                              Select
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Professional Details */}
            <section className="signup-section mb-3">
              <div className="signup-section__header">
                <h5 className="mb-1">Professional Details</h5>
                <p className="text-muted mb-0">Tell us about your background and public profile.</p>
              </div>
              {isRealEstateProfession && (
                <div className="row g-1 mt-1">
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="license_number"
                    label="Real Estate License #"
                    value={formData.license_number}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="start_date"
                    label="Licensed Date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                </div>
              )}

              {/* <div className="row g-1 mt-1">
                <FormInput
                  colClass="col-12 col-md-6 col-xl-4"
                  name="profile_picture"
                  label="Profile Picture URL"
                  type="url"
                  value={formData.profile_picture}
                  onChange={handleInputChange}
                  errors={errors}
                />
              </div>

              {formData.profile_picture && (
                <div className="profile-preview text-center mt-3">
                  <span className="form-label d-block mb-2">Live Profile Preview</span>
                  <img
                    src={formData.profile_picture}
                    alt="Profile Preview"
                    className="img-thumbnail"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )} */}

              <div className="row g-1 mt-1">
                <div className="col-12">
                  <label className="form-label">Short Bio</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="3"
                    placeholder="Introduce yourself and highlight how you support clients."
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </section>

            {isRealEstateProfession && (
              <section className="signup-section mb-3">
                <div className="signup-section__header">
                  <h5 className="mb-1">Brokerage & Office</h5>
                  <p className="text-muted mb-0">Connect your account to your current brokerage.</p>
                </div>
                <div className="row g-1 mt-1">
                  <div className="col-12 col-md-6 col-xl-4 position-relative">
                    <label className="form-label">Brokerage</label>
                    <input
                      type="text"
                      name="office_business_name"
                      value={formData.office_business_name}
                      className={`form-control form-control-sm ${errors?.office_business_name ? "is-invalid" : ""}`}
                      placeholder="Start typing brokerage..."
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange(e);
                        setQuery(value);
                        setShow(true);
                      }}
                      autoComplete="off"
                    />
                    {errors?.office_business_name && (
                      <div className="invalid-feedback">{errors.office_business_name}</div>
                    )}
                    {query?.length >= 2 && show && results.length > 0 && (
                      <ul className="list-group position-absolute w-100 signup-autocomplete">
                        {results.map((b) => (
                          <li
                            key={b.id}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                office_id: b.office_id,
                                office_business_name: b.office_business_name,
                                broker: b.broker,
                                office_city: b.office_city,
                                office_state: b.office_state,
                                office_street_country: "US",
                                phone_number: b.phone_number,
                                broker_email: b.broker_email || "",
                              }));
                              setQuery(b.office_business_name);
                              setShow(false);
                            }}
                            role="button"
                          >
                            <strong>{b.office_business_name}</strong>
                            <div className="text-muted small">
                              {b.office_city}, {b.office_state} • {b.broker}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="broker"
                    label="Designated Broker"
                    value={formData.broker || ""}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="office_city"
                    label="Office City"
                    value={formData.office_city || ""}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="office_state"
                    label="Office State"
                    value={formData.office_state || ""}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="phone_number"
                    label="Office Phone"
                    value={formData.phone_number || ""}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                  <FormInput
                    colClass="col-12 col-md-6 col-xl-4"
                    name="broker_email"
                    label="Broker Email"
                    value={formData.broker_email || ""}
                    onChange={handleInputChange}
                    errors={errors}
                  />
                </div>
              </section>
            )}

            {/* Account Security */}
            <section className="signup-section mb-3">
              <div className="signup-section__header">
                <h5 className="mb-1">Account Security</h5>
                <p className="text-muted mb-0">Secure your account with a strong password.</p>
              </div>
              <div className="row g-1 mt-1">
                <FormInput
                  colClass="col-12 col-md-6"
                  name="password"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  errors={errors}
                />
                <FormInput
                  colClass="col-12 col-md-6"
                  name="confirm_password"
                  label="Confirm Password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  errors={errors}
                  helperText={
                    formData.confirm_password &&
                    formData.password !== formData.confirm_password
                      ? "Passwords do not match."
                      : formData.confirm_password &&
                        formData.password === formData.confirm_password
                        ? "✅ Passwords match"
                        : ""
                  }
                />
              </div>
            </section>

            <section className="signup-section mb-3">
              <div className="signup-section__header">
                <h5 className="mb-1">Subscription</h5>
                <p className="text-muted mb-0">
                  All new accounts continue to Stripe checkout to start the 14-day free trial, then $7/month after.
                </p>
              </div>
            </section>

            <div className="form-check mt-2">
              <input
                className={`form-check-input ${errors.agreeToTerms ? "is-invalid" : ""}`}
                type="checkbox"
                id="agreeToTerms"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="agreeToTerms">
                I agree to the{" "}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => setShowTermsModal(true)}
                >
                  Terms and Conditions
                </button>
              </label>
              {errors.agreeToTerms && (
                <div className="invalid-feedback d-block">{errors.agreeToTerms}</div>
              )}
            </div>
            <TermsModal show={showTermsModal} onHide={() => setShowTermsModal(false)} />

            <div className="d-flex justify-content-center mt-4">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                <FaSave className="me-2" /> {submitting ? "Creating..." : "Create Account"}
              </button>
            </div>
            {errors.form && (
              <div className="text-danger text-center mt-3">{errors.form}</div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// 🔹 Reusable Form Input Component
const FormInput = ({
  name,
  label,
  type = "text",
  value,
  onChange,
  errors,
  helperText,
  readOnly = false,
  colClass = "col-12 col-sm-6 col-lg-4",
}) => (
  <div className={colClass}>
    <label className="form-label">
      {label || name.charAt(0).toUpperCase() + name.slice(1)}
    </label>
    <input
      type={type}
      className={`form-control form-control-sm ${errors?.[name] ? "is-invalid" : ""}`}
      placeholder={label || name}
      name={name}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
    />
    {errors?.[name] && <div className="invalid-feedback">{errors[name]}</div>}
    {helperText && !errors?.[name] && (
      <div className="form-text text-muted">{helperText}</div>
    )}
  </div>
);

export default SignUp;
